import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getMyProfile } from '../api';
import { authUtils } from '../supabase';
import PaymentModal from './PaymentModal';
import './Profile.css';

interface UserProfile {
  id: number;
  email: string;
  display_name: string;
  created_at: string;
  last_login_at: string;
  identities: Array<{
    provider: string;
    email: string;
    linked_at: string;
  }>;
  games_stats: {
    total: number;
    finished: number;
    active: number;
  };
  games_balance: {
    free_games_remaining: number;
    paid_games_remaining: number;
  };
}

const Profile: React.FC = () => {
  const { user, signOut } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const profileData = await getMyProfile();
      setProfile(profileData);
    } catch (err) {
      setError('Ошибка загрузки профиля');
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const openBillingPortal = () => {
    // Stripe Customer Portal для управления подпиской
    window.open('https://billing.stripe.com/p/login/28E8wH3WucDD4ou1XN3Ru00', '_blank');
  };

  useEffect(() => {
    if (user) {
      loadProfile();
    }
  }, [user]);

  const handleSignOut = async () => {
    try {
      const { error } = await signOut();
      if (error) {
        console.error('Sign out error:', error);
        // Показываем пользователю ошибку, но все равно перенаправляем
        setError('Ошибка при выходе из аккаунта, но вы были разлогинены');
      }
      // Перенаправляем на главную страницу (даже при ошибке)
      window.location.href = '/';
    } catch (err) {
      console.error('Error signing out:', err);
      setError('Ошибка при выходе из аккаунта, но вы были разлогинены');
      // Перенаправляем на главную страницу даже при ошибке
      window.location.href = '/';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProviderDisplayName = (provider: string) => {
    switch (provider) {
      case 'google':
        return 'Google';
      case 'email':
        return 'Email';
      default:
        return provider;
    }
  };

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'google':
        return '🔍';
      case 'email':
        return '📧';
      default:
        return '🔑';
    }
  };

  if (!user) {
    return (
      <div className="profile-container">
        <div className="auth-required">
          <h2>Войдите в аккаунт</h2>
          <p>Для просмотра профиля необходимо войти в аккаунт</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>Мой профиль</h1>
        <button 
          className="sign-out-btn"
          onClick={handleSignOut}
        >
          Выйти
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading">
          Загрузка профиля...
        </div>
      ) : profile ? (
        <div className="profile-content">
          {/* Основная информация */}
          <div className="profile-section">
            <h2>Основная информация</h2>
            <div className="profile-info">
              <div className="info-item">
                <label>Имя:</label>
                <span>{profile.display_name || 'Не указано'}</span>
              </div>
              <div className="info-item">
                <label>Email:</label>
                <span>{profile.email}</span>
              </div>
              <div className="info-item">
                <label>ID пользователя:</label>
                <span className="user-id">#{profile.id}</span>
              </div>
              <div className="info-item">
                <label>Дата регистрации:</label>
                <span>{formatDate(profile.created_at)}</span>
              </div>
              <div className="info-item">
                <label>Последний вход:</label>
                <span>{formatDate(profile.last_login_at)}</span>
              </div>
            </div>
          </div>

          {/* Способы входа */}
          <div className="profile-section">
            <h2>Способы входа</h2>
            <div className="identities-list">
              {profile.identities.map((identity, index) => (
                <div key={index} className="identity-item">
                  <div className="identity-icon">
                    {getProviderIcon(identity.provider)}
                  </div>
                  <div className="identity-info">
                    <div className="identity-provider">
                      {getProviderDisplayName(identity.provider)}
                    </div>
                    <div className="identity-email">
                      {identity.email}
                    </div>
                    <div className="identity-date">
                      Подключен: {formatDate(identity.linked_at)}
                    </div>
                  </div>
                  <div className="identity-status">
                    <span className="status-connected">Подключен</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Статистика игр */}
          <div className="profile-section">
            <h2>Статистика игр</h2>
            <div className="games-stats">
              <div className="stat-item">
                <div className="stat-number">{profile.games_stats.total}</div>
                <div className="stat-label">Всего игр</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{profile.games_stats.active}</div>
                <div className="stat-label">Активных</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{profile.games_stats.finished}</div>
                <div className="stat-label">Завершенных</div>
              </div>
            </div>
          </div>

          {/* Баланс игр */}
          <div className="profile-section">
            <h2>Баланс игр</h2>
            <div className="games-balance">
              <div className="balance-item free-games">
                <div className="balance-icon">🆓</div>
                <div className="balance-info">
                  <div className="balance-number">{profile.games_balance.free_games_remaining}</div>
                  <div className="balance-label">Бесплатные игры</div>
                  <div className="balance-description">Для создания новых игр до первой 6-ки</div>
                </div>
              </div>
              <div className="balance-item paid-games">
                <div className="balance-icon">💰</div>
                <div className="balance-info">
                  <div className="balance-number">{profile.games_balance.paid_games_remaining}</div>
                  <div className="balance-label">Платные игры</div>
                  <div className="balance-description">Для продолжения после принятия запроса</div>
                </div>
              </div>
            </div>

            {/* Кнопка покупки игр */}
            <div className="buy-games-section">
              <button
                className="buy-games-btn"
                onClick={() => setShowPaymentModal(true)}
              >
                <span className="btn-icon">🛒</span>
                Купить игры
              </button>
              <p className="buy-games-hint">
                Купите игры разово или оформите подписку для регулярной игры
              </p>
            </div>
          </div>

          {/* Управление подпиской */}
          {profile.games_balance.paid_games_remaining > 0 && (
            <div className="profile-section">
              <h2>Управление подпиской</h2>
              <div className="subscription-management">
                <p>Управляйте своей подпиской, обновляйте платежные данные и скачивайте чеки.</p>
                <button
                  className="billing-portal-btn"
                  onClick={openBillingPortal}
                >
                  <span className="btn-icon">⚙️</span>
                  Управление подпиской и платежами
                </button>
              </div>
            </div>
          )}

          {/* Дополнительная информация */}
          <div className="profile-section">
            <h2>О системе</h2>
            <div className="system-info">
              <p>
                <strong>Прогресс сохраняется в вашем аккаунте</strong>
              </p>
              <p>
                Все ваши игры и прогресс автоматически сохраняются и синхронизируются 
                между устройствами. Вы можете продолжить игру с любого устройства, 
                войдя в свой аккаунт.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="no-profile">
          <h3>Профиль не найден</h3>
          <p>Не удалось загрузить информацию о профиле</p>
        </div>
      )}

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onPaymentSuccess={() => {
          setShowPaymentModal(false);
          loadProfile(); // Перезагружаем профиль после оплаты
        }}
      />
    </div>
  );
};

export default Profile;
